import React from 'react';
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Code2,
  FileSpreadsheet,
  FileText,
  ImagePlus,
  Plus,
  Settings2,
  Type,
  Upload,
} from 'lucide-react';
import { validateManualRequirementInput } from '../../utils/validators';

const BRD_DOCUMENT_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/x-msword',
  'application/word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

const OPENAPI_DOCUMENT_ACCEPT = [
  '.json',
  '.yaml',
  '.yml',
  'application/json',
  'application/yaml',
  'application/x-yaml',
  'text/yaml',
  'text/x-yaml',
].join(',');

const EXCEL_TEST_CASE_ACCEPT = [
  '.xlsx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

const openFilePickerFromKeyboard = (event, inputId) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  document.getElementById(inputId)?.click();
};

export const PDFUploadStep = ({
  intakeMode = 'combined',
  file,
  openApiFile = null,
  excelFile = null,
  manualInputTitle = '',
  manualInputText = '',
  manualInputImages = [],
  status,
  activeUploadSource = '',
  onFileChange,
  onUpload,
  onOpenApiFileChange,
  onUploadOpenApi,
  onExcelFileChange,
  onAnalyzeExcel,
  onManualInputTitleChange,
  onManualInputTextChange,
  onManualInputImagesChange,
  onUploadTextImages,
  workspaces = [],
  selectedWorkspaceId = '',
  workspaceLoading = false,
  workspaceError = '',
  onSelectWorkspace,
  onCreateWorkspace,
  onManageWorkspaceMembers,
  canCreateWorkspace = false,
  canViewWorkspaceMembers = false,
  applicationLocked = false,
}) => {
  const normalizedIntakeMode = ['document', 'text', 'api', 'excel'].includes(intakeMode)
    ? intakeMode
    : 'combined';
  const defaultSource = normalizedIntakeMode === 'text'
    ? 'text'
    : normalizedIntakeMode === 'api'
      ? 'api'
      : normalizedIntakeMode === 'excel'
        ? 'excel'
        : 'pdf';
  const preferredInitialSource = defaultSource;
  const [newWorkspaceName, setNewWorkspaceName] = React.useState('');
  const [creatingWorkspace, setCreatingWorkspace] = React.useState(false);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [activeSource, setActiveSource] = React.useState(preferredInitialSource);
  const [showWorkspaceTools, setShowWorkspaceTools] = React.useState(false);
  const selectedWorkspace = React.useMemo(
    () => workspaces.find((workspace) => String(workspace.id) === String(selectedWorkspaceId)),
    [selectedWorkspaceId, workspaces]
  );
  const manualInputLength = String(manualInputText || '').trim().length;
  const manualInputValidation = React.useMemo(
    () => validateManualRequirementInput(manualInputText, manualInputTitle),
    [manualInputText, manualInputTitle]
  );
  const shouldShowManualInputFeedback = Boolean(
    String(manualInputText || '').trim() || String(manualInputTitle || '').trim()
  );
  const manualInputFeedbackId = shouldShowManualInputFeedback ? 'manual-input-feedback' : undefined;
  const isUploading = status === 'uploading';
  const canUseWorkspaceTools = canCreateWorkspace || canViewWorkspaceMembers;
  const opensMembersDirectly = canViewWorkspaceMembers && !canCreateWorkspace;
  const getWorkspaceDisplayRole = (workspace) => (
    String(workspace?.current_user_role || 'member').trim().toLowerCase()
  );
  const isDocumentUploading =
    isUploading && (activeUploadSource === 'document' || !activeUploadSource);
  const isTextImagesUploading = isUploading && activeUploadSource === 'text-images';
  const isOpenApiUploading = isUploading && activeUploadSource === 'openapi';
  const isExcelUploading = isUploading && activeUploadSource === 'testcase-excel';
  const canUploadDocument = Boolean(file && !isUploading && selectedWorkspaceId);
  const canUploadOpenApi = Boolean(openApiFile && !isUploading && selectedWorkspaceId);
  const canAnalyzeExcel = Boolean(excelFile && !isUploading && selectedWorkspaceId);
  const sourceOptions = [
    {
      id: 'pdf',
      label: 'Document',
      description: file ? file.name : 'Upload a PDF or Word BRD',
      icon: FileText,
      ready: Boolean(file),
    },
    {
      id: 'text',
      label: 'Text',
      description: manualInputValidation.valid
        ? `${manualInputLength} characters ready`
        : manualInputLength > 0
          ? 'Needs requirement details'
          : 'Enter requirements',
      icon: Type,
      ready: manualInputValidation.valid,
    },
    {
      id: 'api',
      label: 'Swagger',
      description: openApiFile ? openApiFile.name : 'Upload OpenAPI or Swagger',
      icon: Code2,
      ready: Boolean(openApiFile),
    },
    {
      id: 'excel',
      label: 'Excel Cases',
      description: excelFile ? excelFile.name : 'Import existing .xlsx testcases',
      icon: FileSpreadsheet,
      ready: Boolean(excelFile),
    },
  ].filter((option) => (
    normalizedIntakeMode === 'combined'
    || (normalizedIntakeMode === 'document' && option.id === 'pdf')
    || (normalizedIntakeMode === 'text' && option.id === 'text')
    || (normalizedIntakeMode === 'api' && option.id === 'api')
    || (normalizedIntakeMode === 'excel' && option.id === 'excel')
  ));
  const showSourceSelector = sourceOptions.length > 1;
  const selectedSourceOption = sourceOptions.find((option) => option.id === activeSource) || sourceOptions[0];
  const SelectedSourceIcon = selectedSourceOption?.icon || FileText;

  React.useEffect(() => {
    setActiveSource(preferredInitialSource);
  }, [preferredInitialSource]);

  const preventDragDefaults = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = React.useCallback((event) => {
    preventDragDefaults(event);
    setIsDragActive(true);
  }, [preventDragDefaults]);

  const handleDragLeave = React.useCallback((event) => {
    preventDragDefaults(event);
    const currentTarget = event.currentTarget;
    const nextTarget = event.relatedTarget;
    if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) {
      return;
    }
    setIsDragActive(false);
  }, [preventDragDefaults]);

  const handleDrop = React.useCallback((event) => {
    preventDragDefaults(event);
    setIsDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0];
    if (!droppedFile) return;
    onFileChange?.(droppedFile);
  }, [onFileChange, preventDragDefaults]);

  const handleExcelDrop = React.useCallback((event) => {
    preventDragDefaults(event);
    setIsDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0];
    if (!droppedFile) return;
    onExcelFileChange?.(droppedFile);
  }, [onExcelFileChange, preventDragDefaults]);

  const handleCreateWorkspace = async () => {
    const trimmedName = String(newWorkspaceName || '').trim();
    if (!canCreateWorkspace || !trimmedName || !onCreateWorkspace) return;
    setCreatingWorkspace(true);
    try {
      await onCreateWorkspace({ name: trimmedName, source_type: 'application' });
      setNewWorkspaceName('');
    } catch (_err) {
      // Parent workflow surfaces application errors.
    } finally {
      setCreatingWorkspace(false);
    }
  };

  return (
    <div className={`workspace-intake workspace-intake-${normalizedIntakeMode} space-y-5`}>
      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="workspace-context-icon inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white">
              <Briefcase className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Application
              </p>
              <h3 className="truncate text-base font-semibold text-slate-900">
                {selectedWorkspace?.name || 'Select an application'}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {selectedWorkspace
                  ? `${getWorkspaceDisplayRole(selectedWorkspace)} access`
                  : 'Required before creating a source'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {!applicationLocked && (
              <select
                className="text-input min-w-[240px] bg-white"
                value={selectedWorkspaceId}
                onChange={(event) => onSelectWorkspace?.(event.target.value)}
                disabled={workspaceLoading}
                aria-label="Select application"
              >
                <option value="">Choose an application...</option>
                {workspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name} ({getWorkspaceDisplayRole(workspace)})
                  </option>
                ))}
              </select>
            )}
            {!applicationLocked && canUseWorkspaceTools && (
              <button
                type="button"
                className="secondary-btn px-3 py-2 text-sm"
                onClick={() => {
                  if (opensMembersDirectly) {
                    onManageWorkspaceMembers?.();
                    return;
                  }
                  setShowWorkspaceTools((previous) => !previous);
                }}
                aria-expanded={opensMembersDirectly ? undefined : showWorkspaceTools}
              >
                <Settings2 className="h-4 w-4" />
                {opensMembersDirectly ? 'Members' : 'Manage'}
                {!opensMembersDirectly && (
                  showWorkspaceTools ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )
                )}
              </button>
            )}
          </div>
        </div>

        {workspaceError && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {workspaceError}
          </div>
        )}

        {!applicationLocked && showWorkspaceTools && canUseWorkspaceTools && !opensMembersDirectly && (
          <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-[1fr_auto]">
            {canCreateWorkspace && (
              <div className="min-w-0">
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Create Application
                </label>
                <input
                  className="text-input"
                  placeholder="New application name"
                  value={newWorkspaceName}
                  onChange={(event) => setNewWorkspaceName(event.target.value)}
                />
              </div>
            )}
            <div className="flex items-end gap-2">
              {canCreateWorkspace && (
                <button
                  type="button"
                  className="secondary-btn px-3 py-2 text-sm"
                  onClick={handleCreateWorkspace}
                  disabled={creatingWorkspace || !String(newWorkspaceName || '').trim()}
                >
                  <Plus className="h-4 w-4" />
                  {creatingWorkspace ? 'Creating...' : 'Create'}
                </button>
              )}
              {canViewWorkspaceMembers && selectedWorkspaceId && (
                <button
                  type="button"
                  className="secondary-btn px-3 py-2 text-sm"
                  onClick={() => onManageWorkspaceMembers?.()}
                >
                  <Settings2 className="h-4 w-4" />
                  Members
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="soft-card p-0">
        <div className="border-b border-slate-200 p-4 md:p-5">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Source Intake
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                {normalizedIntakeMode === 'text'
                  ? 'Describe the feature to test'
                  : normalizedIntakeMode === 'api'
                    ? 'Upload your API contract'
                    : normalizedIntakeMode === 'excel'
                      ? 'Upload existing test cases'
                      : normalizedIntakeMode === 'document'
                        ? 'Upload a BRD document'
                        : 'Choose one way to start'}
              </h3>
              {!showSourceSelector && (
                <p className="mt-1 text-sm text-slate-500">
                  {normalizedIntakeMode === 'text'
                    ? 'Add focused requirements for one feature, workflow, or module.'
                    : normalizedIntakeMode === 'api'
                      ? 'Add a Swagger or OpenAPI file to generate API test cases.'
                      : normalizedIntakeMode === 'excel'
                        ? 'Add an XLSX workbook to import existing test cases.'
                        : 'Add a PDF or Word document to begin section analysis.'}
                </p>
              )}
            </div>
            {showSourceSelector ? (
              <div className={`grid gap-2 ${sourceOptions.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {sourceOptions.map((option) => {
                  const OptionIcon = option.icon;
                  const isActive = activeSource === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setActiveSource(option.id)}
                      className={`flex min-h-[76px] items-center gap-3 rounded-xl border px-3 py-2 text-left transition-all ${isActive
                          ? 'border-blue-500 bg-blue-50 shadow-sm ring-4 ring-blue-100'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      aria-pressed={isActive}
                    >
                      <span
                        className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        <OptionIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                          {option.label}
                          {option.ready && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500">
                          {option.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="inline-flex min-h-14 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${normalizedIntakeMode === 'text'
                    ? 'bg-violet-100 text-violet-700'
                    : normalizedIntakeMode === 'api'
                      ? 'bg-sky-100 text-sky-700'
                      : normalizedIntakeMode === 'excel'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-blue-100 text-blue-700'
                  }`}>
                  <SelectedSourceIcon className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-slate-900">
                    {normalizedIntakeMode === 'text'
                      ? 'Text requirements'
                      : normalizedIntakeMode === 'api'
                        ? 'Swagger / OpenAPI'
                        : normalizedIntakeMode === 'excel'
                          ? 'Excel test cases'
                          : 'BRD document'}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {selectedSourceOption?.description}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>

        {activeSource === 'pdf' && (
          <div className="p-4 md:p-5">
            <label
              htmlFor="pdf-upload"
              role="button"
              tabIndex={0}
              aria-describedby="pdf-upload-help"
              className={`workspace-source-dropzone flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                isDragActive ? 'workspace-source-dropzone-active' : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={preventDragDefaults}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={(event) => openFilePickerFromKeyboard(event, 'pdf-upload')}
            >
              <Upload className="mx-auto mb-3 h-12 w-12 text-slate-400" />
              <input
                type="file"
                accept={BRD_DOCUMENT_ACCEPT}
                onChange={onFileChange}
                className="hidden"
                id="pdf-upload"
              />
              <span className="workspace-accent-text text-base font-semibold">
                Click to upload PDF or Word
              </span>
              <p id="pdf-upload-help" className="mt-1 text-xs font-medium text-slate-500">
                Accepted: .pdf, .doc, .docx
              </p>
              <p className="mt-2 text-sm text-slate-500">or drag and drop a BRD document</p>
              {file && (
                <div className="mt-5 flex max-w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
                  <FileText className="h-5 w-5 shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium" title={file.name}>
                    {file.name}
                  </span>
                </div>
              )}
            </label>
            <button
              onClick={onUpload}
              disabled={!canUploadDocument}
              className="primary-btn workspace-accent-button mt-4 w-full py-3"
            >
              {isDocumentUploading ? (
                <>
                  <Upload className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Document
                </>
              )}
            </button>
            {!selectedWorkspaceId && (
              <p className="mt-2 text-xs text-amber-700">
                Select an application before uploading a BRD.
              </p>
            )}
          </div>
        )}

        {activeSource === 'excel' && (
          <div className="p-4 md:p-5">
            <label
              htmlFor="testcase-excel-upload"
              role="button"
              tabIndex={0}
              aria-describedby="testcase-excel-upload-help"
              className={`workspace-source-dropzone flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                isDragActive ? 'workspace-source-dropzone-active' : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={preventDragDefaults}
              onDragLeave={handleDragLeave}
              onDrop={handleExcelDrop}
              onKeyDown={(event) => openFilePickerFromKeyboard(event, 'testcase-excel-upload')}
            >
              <FileSpreadsheet className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
              <input
                type="file"
                accept={EXCEL_TEST_CASE_ACCEPT}
                onChange={onExcelFileChange}
                className="hidden"
                id="testcase-excel-upload"
              />
              <span className="workspace-accent-text text-base font-semibold">
                Select an Excel testcase workbook
              </span>
              <p id="testcase-excel-upload-help" className="mt-1 text-xs font-medium text-slate-500">
                Accepted: .xlsx
              </p>
              <p className="mt-2 max-w-xl text-sm text-slate-500">
                Drag and drop or select a workbook. Sheet names and columns may vary; you will review the detected header row and field mappings before import.
              </p>
              {excelFile && (
                <div className="mt-5 flex max-w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
                  <FileSpreadsheet className="h-5 w-5 shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium" title={excelFile.name}>
                    {excelFile.name}
                  </span>
                </div>
              )}
            </label>
            <button
              type="button"
              onClick={onAnalyzeExcel}
              disabled={!canAnalyzeExcel}
              className="primary-btn workspace-accent-button mt-4 w-full py-3"
            >
              {isExcelUploading ? (
                <>
                  <Upload className="h-5 w-5 animate-spin" />
                  Analyzing workbook...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-5 w-5" />
                  Analyze Test Cases
                </>
              )}
            </button>
            {!selectedWorkspaceId && (
              <p className="mt-2 text-xs text-amber-700">
                Select an application before importing test cases.
              </p>
            )}
          </div>
        )}

        {activeSource === 'text' && (
          <div className="p-4 md:p-5">
            <div className="flex min-h-[300px] flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 md:p-5">
              <div>
                <label htmlFor="manual-requirement-title" className="mb-1.5 block text-sm font-semibold text-slate-800">
                  Feature or module name <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <input
                  id="manual-requirement-title"
                  className={`text-input bg-white ${shouldShowManualInputFeedback && !manualInputValidation.valid ? 'border-amber-300 focus:border-amber-500 focus:ring-amber-100' : ''}`}
                  placeholder="Example: Loan application approval"
                  value={manualInputTitle}
                  onChange={(event) => onManualInputTitleChange?.(event.target.value)}
                  aria-invalid={shouldShowManualInputFeedback && !manualInputValidation.valid}
                  aria-describedby={manualInputFeedbackId}
                />
              </div>
              <div className="flex flex-1 flex-col">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label htmlFor="manual-requirement-text" className="text-sm font-semibold text-slate-800">
                    Requirement details
                  </label>
                  <span className="text-xs font-medium tabular-nums text-slate-400">
                    {manualInputLength.toLocaleString()} characters
                  </span>
                </div>
                <textarea
                  id="manual-requirement-text"
                  className={`text-input min-h-[190px] flex-1 resize-y bg-white leading-6 ${shouldShowManualInputFeedback && !manualInputValidation.valid ? 'border-amber-300 focus:border-amber-500 focus:ring-amber-100' : ''}`}
                  placeholder="Paste the user journey, business rules, validations, acceptance criteria, and expected results"
                  value={manualInputText}
                  onChange={(event) => onManualInputTextChange?.(event.target.value)}
                  aria-invalid={shouldShowManualInputFeedback && !manualInputValidation.valid}
                  aria-describedby={manualInputFeedbackId}
                />
              </div>
              {shouldShowManualInputFeedback && (
                <div
                  id="manual-input-feedback"
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${manualInputValidation.valid
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                >
                  {manualInputValidation.valid ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  ) : (
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium">
                      {manualInputValidation.valid
                        ? 'Requirement details look ready.'
                        : manualInputValidation.message}
                    </p>
                    {!manualInputValidation.valid && (
                      <p className="mt-1 text-xs leading-5 text-amber-700">
                        Include who is using the feature, what they do, and what result or validation should happen.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="secondary-btn cursor-pointer justify-center px-3 py-2 text-sm">
                  <ImagePlus className="h-4 w-4" />
                  Add Images
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    onChange={onManualInputImagesChange}
                    className="hidden"
                  />
                </label>
                <p className="text-xs font-medium text-emerald-700">
                  {manualInputImages.length > 0
                    ? `${manualInputImages.length} image${manualInputImages.length === 1 ? '' : 's'} selected`
                    : 'Screenshots are optional'}
                </p>
              </div>
            </div>
            <button
              onClick={onUploadTextImages}
              disabled={
                isUploading ||
                !selectedWorkspaceId ||
                !manualInputValidation.valid
              }
              className="primary-btn workspace-accent-button mt-4 w-full py-3"
            >
              {isTextImagesUploading ? (
                <>
                  <Upload className="h-5 w-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Type className="h-5 w-5" />
                  Create Requirement Source
                </>
              )}
            </button>
            {!selectedWorkspaceId && (
              <p className="mt-2 text-xs text-amber-700">
                Select an application before creating a source.
              </p>
            )}
          </div>
        )}


        {activeSource === 'api' && (
          <div className="p-4 md:p-5">
            <label
              htmlFor="openapi-upload"
              role="button"
              tabIndex={0}
              aria-describedby="openapi-upload-help"
              onKeyDown={(event) => openFilePickerFromKeyboard(event, 'openapi-upload')}
              className="workspace-source-dropzone flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors"
            >
              <Code2 className="workspace-accent-text mx-auto mb-3 h-12 w-12" />
              <input
                type="file"
                accept={OPENAPI_DOCUMENT_ACCEPT}
                onChange={onOpenApiFileChange}
                className="hidden"
                id="openapi-upload"
              />
              <span className="workspace-accent-text text-base font-semibold">
                Click to upload Swagger / OpenAPI
              </span>
              <p id="openapi-upload-help" className="mt-1 text-xs font-medium text-slate-500">
                Accepted: .json, .yaml, .yml
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Each endpoint operation becomes a selectable test-generation unit.
              </p>
              {openApiFile && (
                <div className="mt-5 flex max-w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
                  <Code2 className="h-5 w-5 shrink-0" />
                  <span className="min-w-0 truncate text-sm font-medium" title={openApiFile.name}>
                    {openApiFile.name}
                  </span>
                </div>
              )}
            </label>
            <button
              onClick={onUploadOpenApi}
              disabled={!canUploadOpenApi}
              className="primary-btn workspace-accent-button mt-4 w-full py-3"
            >
              {isOpenApiUploading ? (
                <>
                  <Upload className="h-5 w-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Code2 className="h-5 w-5" />
                  Upload Swagger
                </>
              )}
            </button>
            {!selectedWorkspaceId && (
              <p className="mt-2 text-xs text-amber-700">
                Select an application before uploading an API contract.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
